/**
 * ClashSettings - Cribbage Clash 481 UI Options & Overlap Prevention System
 * Encapsulates hazard block definitions, active obstacle resolution,
 * conflict-free dropdown updates, and collision prevention.
 */

import { PINTS_DRINKING_DEFS } from './PintsSettings.js';
import { getClashDefaults } from '../board/ObstacleGeometry.js';
import { ClashPresets } from './ClashPresets.js';
import { ClashSandboxModal } from './ClashSandboxModal.js';

export class ClashSettings {
  static applyPreset(presetKey, context = {}) {
    return ClashPresets.applyPreset(presetKey, context);
  }

  static openSandbox(context = {}) {
    return ClashSandboxModal.open(context);
  }

  static getBlockDefs() {
    const baseDefs = [
      {
        type: 'noPassingLanes',
        chkId: 'optNoPassingLanes',
        countId: 'optNoPassingLaneCount',
        maxCount: 4,
        baseName: 'No Passing Lane',
        selectIdPrefix: 'optNoPassingLane',
        selectIdSuffix: 'Start',
        getLength: () => 5,
        minHole: 10,
        maxHole: 100,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onNoPassingLaneFieldChange === 'function') {
            window.onNoPassingLaneFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'jump',
        chkId: 'optJump',
        countId: 'optJumpCount',
        maxCount: 8,
        baseName: 'Jump Zone',
        selectIdPrefix: 'optJump',
        selectIdSuffix: 'Start',
        getLength: (idx) => {
          if (typeof document === 'undefined') return 1;
          const sEl = document.getElementById(`optJump${idx}Start`);
          const dEl = document.getElementById(`optJump${idx}Dest`);
          const sVal = sEl ? parseInt(sEl.value, 10) : 0;
          const dVal = dEl ? parseInt(dEl.value, 10) : 0;
          return (!isNaN(sVal) && !isNaN(dVal) && sVal > 0 && dVal > 0 && dVal <= sVal) ? 3 : 1;
        },
        minHole: 5,
        maxHole: 115,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onJumpFieldChange === 'function') {
            window.onJumpFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'jail',
        chkId: 'optJail',
        countId: 'optJailCount',
        maxCount: 4,
        baseName: 'Jail',
        selectIdPrefix: 'optJail',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 10,
        maxHole: 110,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onJailFieldChange === 'function') {
            window.onJailFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'bar',
        chkId: 'optBar',
        countId: 'optBarCount',
        maxCount: 4,
        baseName: 'The Bar',
        selectIdPrefix: 'optBar',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 10,
        maxHole: 110,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onBarFieldChange === 'function') {
            window.onBarFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'timeout',
        chkId: 'optTimeout',
        countId: 'optTimeoutCount',
        maxCount: 4,
        baseName: 'Timeout',
        selectIdPrefix: 'optTimeout',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 10,
        maxHole: 110,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onTimeoutFieldChange === 'function') {
            window.onTimeoutFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'detour',
        chkId: 'optDetour',
        countId: 'optDetourCount',
        maxCount: 4,
        baseName: 'Detour',
        selectIdPrefix: 'optDetour',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 10,
        maxHole: 110,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onDetourFieldChange === 'function') {
            window.onDetourFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'roundAbout',
        chkId: 'optRoundAbout',
        countId: 'optRoundAboutCount',
        maxCount: 4,
        baseName: 'Round About',
        selectIdPrefix: 'optRoundAbout',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 10,
        maxHole: 110,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onRoundAboutFieldChange === 'function') {
            window.onRoundAboutFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'signs',
        chkId: 'optSigns',
        countId: 'optSignsCount',
        maxCount: 8,
        baseName: 'Sign Block',
        selectIdPrefix: 'optSigns',
        selectIdSuffix: 'Hole',
        getLength: (idx) => {
          if (typeof document === 'undefined') return 3;
          const sizeEl = document.getElementById(`optSigns${idx}Size`);
          const sz = sizeEl ? parseInt(sizeEl.value, 10) : 2;
          return sz === 4 ? 5 : 3;
        },
        minHole: 10,
        maxHole: 110,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onSignsFieldChange === 'function') {
            window.onSignsFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'stopSign',
        chkId: 'optStopSign',
        countId: 'optStopSignCount',
        maxCount: 8,
        baseName: 'Stop Sign',
        selectIdPrefix: 'optStopSign',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 5,
        maxHole: 475,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onStopSignFieldChange === 'function') {
            window.onStopSignFieldChange(idx, skipSave);
          }
        }
      },
      {
        type: 'dice',
        chkId: 'optDice',
        countId: 'optDiceCount',
        maxCount: 4,
        baseName: 'Dice',
        selectIdPrefix: 'optDice',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 5,
        maxHole: 475,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onDiceFieldChange === 'function') {
            window.onDiceFieldChange(idx, skipSave);
          }
        }
      },
      { 
        type: 'wheel',
        chkId: 'optWheel',
        countId: 'optWheelCount',
        maxCount: 4,
        baseName: 'Spinning Wheel',
        selectIdPrefix: 'optWheel',
        selectIdSuffix: 'Hole',
        getLength: () => 1,
        minHole: 5,
        maxHole: 475,
        onFieldChange: (idx, skipSave) => {
          if (typeof window !== 'undefined' && typeof window.onWheelFieldChange === 'function') {
            window.onWheelFieldChange(idx, skipSave);
          }
        }
      }
    ];

    const drinkingDefs = (PINTS_DRINKING_DEFS || []).map(dt => ({
      type: dt.key,
      chkId: dt.chkId,
      countId: dt.countId,
      pegsId: dt.pegsId,
      maxCount: 4,
      baseName: dt.name,
      selectIdPrefix: dt.holeIdPrefix,
      selectIdSuffix: 'Hole',
      getLength: () => {
        if (typeof document === 'undefined') return 1;
        return parseInt(document.getElementById(dt.pegsId)?.value || (typeof window !== 'undefined' && window.settings && window.settings[dt.key + 'Pegs']) || 1, 10);
      },
      minHole: 5,
      maxHole: 120,
      onFieldChange: (idx, skipSave) => {
        const fnName = 'on' + dt.key.charAt(0).toUpperCase() + dt.key.slice(1) + 'FieldChange';
        if (typeof window !== 'undefined' && typeof window[fnName] === 'function') {
          window[fnName](idx, skipSave);
        }
      }
    }));

    return [...baseDefs, ...drinkingDefs];
  }

  static getActiveBlocks(blockDefs = null) {
    if (typeof document === 'undefined') return [];
    const defs = blockDefs || ClashSettings.getBlockDefs();
    const active = [];
    for (const def of defs) {
      const chk = document.getElementById(def.chkId);
      if (!chk || !chk.checked) continue;
      const countEl = document.getElementById(def.countId);
      const count = countEl ? Math.min(def.maxCount, Math.max(1, parseInt(countEl.value, 10) || 1)) : 1;
      for (let i = 1; i <= count; i++) {
        const selId = `${def.selectIdPrefix}${i}${def.selectIdSuffix}`;
        const sel = document.getElementById(selId);
        if (!sel) continue;
        const start = parseInt(sel.value, 10);
        if (isNaN(start)) continue;
        const length = typeof def.getLength === 'function' ? def.getLength(i) : 1;
        const end = start + length - 1;
        active.push({
          type: def.type,
          index: i,
          name: `${def.baseName} ${i}`,
          selectId: selId,
          start,
          length,
          end,
          def
        });
      }
    }
    return active;
  }

  static updateAllDropdowns(blockDefs = null, clashManager = null) {
    if (typeof document === 'undefined') return;
    if (typeof window !== 'undefined' && window._isRandomizingClash) return;
    const defs = blockDefs || ClashSettings.getBlockDefs();
    const active = ClashSettings.getActiveBlocks(defs);

    for (const def of defs) {
      for (let i = 1; i <= def.maxCount; i++) {
        const selId = `${def.selectIdPrefix}${i}${def.selectIdSuffix}`;
        const sel = document.getElementById(selId);
        if (!sel) continue;
        const length = typeof def.getLength === 'function' ? def.getLength(i) : 1;

        for (let optIdx = 0; optIdx < sel.options.length; optIdx++) {
          const opt = sel.options[optIdx];
          const val = parseInt(opt.value, 10);
          if (isNaN(val)) continue;

          if (!opt.dataset.originalText) {
            opt.dataset.originalText = opt.textContent;
          }

          let collision = null;
          if (clashManager && typeof clashManager.checkCollision === 'function') {
            collision = clashManager.checkCollision(val, length, selId, active);
          } else {
            collision = active.find(b => b.selectId !== selId && !(val + length - 1 < b.start || val > b.end));
          }

          if (collision) {
            opt.disabled = true;
            opt.textContent = `${opt.dataset.originalText} (Occupied: ${collision.name})`;
          } else {
            opt.disabled = false;
            opt.textContent = opt.dataset.originalText;
          }
        }
      }
    }
  }

  static sanitizeBlockPlacements(s, clashDefaultsFn = null) {
    if (!s) return s;
    const occupied = new Set();
    const targetVal = Number(s?.target || (typeof document !== 'undefined' && document.getElementById('optTarget') && document.getElementById('optTarget').value) || 121);
    const isClashTarget = [241, 361, 481].includes(targetVal);
    const defFn = clashDefaultsFn || (typeof getClashDefaults === 'function' ? getClashDefaults : null);
    const defs = isClashTarget && defFn ? defFn(targetVal) : null;
    const maxClashHole = isClashTarget ? (targetVal - 11) : 110;
    const maxNplHole = isClashTarget ? (targetVal - 11) : 100;
    const maxJumpHole = isClashTarget ? (targetVal - 11) : 115;
    const maxJumpDestHole = isClashTarget ? (targetVal - 1) : 120;
    const maxStopSignHole = isClashTarget ? (targetVal - 6) : 115;

    function isRangeOccupied(start, len) {
      for (let h = start; h < start + len; h++) {
        if (occupied.has(h)) return true;
      }
      return false;
    }

    function occupyRange(start, len) {
      for (let h = start; h < start + len; h++) {
        occupied.add(h);
      }
    }

    function findFreeStart(desired, len, minHole = 10, maxHole = maxClashHole, step = 5) {
      if (!isRangeOccupied(desired, len)) return desired;
      const maxDelta = isClashTarget ? targetVal : 120;
      for (let delta = step; delta <= maxDelta; delta += step) {
        if (desired + delta <= maxHole && !isRangeOccupied(desired + delta, len)) {
          return desired + delta;
        }
        if (desired - delta >= minHole && !isRangeOccupied(desired - delta, len)) {
          return desired - delta;
        }
      }
      for (let h = minHole; h <= maxHole; h += step) {
        if (!isRangeOccupied(h, len)) return h;
      }
      return desired;
    }

    function setDomValue(id, val) {
      if (typeof document !== 'undefined') {
        const el = document.getElementById(id);
        if (el && el.value !== String(val)) el.value = String(val);
      }
    }

    // 1. No Passing Lanes
    if (s.optNoPassingLanes && Array.isArray(s.noPassingLaneStarts)) {
      const count = Math.min(4, Math.max(1, parseInt(s.noPassingLaneCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.noPassingLaneStarts[i] : [20, 50, 80, 100][i];
        const cur = parseInt(s.noPassingLaneStarts[i], 10) || fallback;
        const valid = findFreeStart(cur, 5, 10, maxNplHole);
        s.noPassingLaneStarts[i] = valid;
        occupyRange(valid, 5);
        setDomValue(`optNoPassingLane${i + 1}Start`, valid);
      }
    }

    // 2. Jump
    if (s.optJump && Array.isArray(s.jumpStarts)) {
      const count = Math.min(8, Math.max(1, parseInt(s.jumpCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.jumpStarts[i] : [15, 65, 5, 115, 25, 75, 45, 95][i];
        const cur = parseInt(s.jumpStarts[i], 10) || fallback;
        const dFallback = defs ? defs.jumpDests[i] : [35, 50, 45, 70, 55, 60, 80, 85][i];
        const dVal = (Array.isArray(s.jumpDests) && parseInt(s.jumpDests[i], 10)) || dFallback;
        const isFwd = !isNaN(dVal) && dVal > cur;
        const jumpLen = isFwd ? 1 : 3;
        const valid = findFreeStart(cur, jumpLen, 5, maxJumpHole);
        s.jumpStarts[i] = valid;
        occupyRange(valid, jumpLen);
        setDomValue(`optJump${i + 1}Start`, valid);
      }
    }
    if (s.optJump && Array.isArray(s.jumpDests)) {
      const count = Math.min(8, Math.max(1, parseInt(s.jumpCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.jumpDests[i] : [35, 50, 45, 70, 55, 60, 80, 85][i];
        const cur = parseInt(s.jumpDests[i], 10) || fallback;
        const sVal = (Array.isArray(s.jumpStarts) && parseInt(s.jumpStarts[i], 10));
        const isFwd = !isNaN(sVal) && cur > sVal;
        const maxDestLimit = isFwd ? Math.min(maxJumpDestHole, targetVal - 11) : maxJumpDestHole;
        const valid = findFreeStart(cur, 1, 5, maxDestLimit);
        s.jumpDests[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optJump${i + 1}Dest`, valid);
      }
    }

    // 3. Jail
    if (s.optJail && Array.isArray(s.jailHoles)) {
      const count = Math.min(2, Math.max(1, parseInt(s.jailCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.jailHoles[i] : (i === 0 ? 25 : 75);
        const cur = parseInt(s.jailHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 10, maxClashHole);
        s.jailHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optJail${i + 1}Hole`, valid);
      }
    }

    // 4. Bar
    if (s.optBar && Array.isArray(s.barHoles)) {
      const count = Math.min(2, Math.max(1, parseInt(s.barCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.barHoles[i] : (i === 0 ? 40 : 85);
        const cur = parseInt(s.barHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 10, maxClashHole);
        s.barHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optBar${i + 1}Hole`, valid);
      }
    }

    // 5. Timeout
    if (s.optTimeout && Array.isArray(s.timeoutHoles)) {
      const count = Math.min(2, Math.max(1, parseInt(s.timeoutCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.timeoutHoles[i] : (i === 0 ? 30 : 90);
        const cur = parseInt(s.timeoutHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 10, maxClashHole);
        s.timeoutHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optTimeout${i + 1}Hole`, valid);
      }
    }

    // 6. Detour
    if (s.optDetour && Array.isArray(s.detourHoles)) {
      const count = Math.min(2, Math.max(1, parseInt(s.detourCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.detourHoles[i] : (i === 0 ? 10 : 60);
        const cur = parseInt(s.detourHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 10, maxClashHole);
        s.detourHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optDetour${i + 1}Hole`, valid);
      }
    }

    // 7. Round About
    if (s.optRoundAbout && Array.isArray(s.roundAboutHoles)) {
      const count = Math.min(2, Math.max(1, parseInt(s.roundAboutCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defs ? defs.roundAboutHoles[i] : (i === 0 ? 35 : 70);
        const cur = parseInt(s.roundAboutHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 10, maxClashHole);
        s.roundAboutHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optRoundAbout${i + 1}Hole`, valid);
      }
    }

    // 8. Signs
    if (s.optSigns && Array.isArray(s.signsBlocks)) {
      const count = Math.min(4, Math.max(1, parseInt(s.signsCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const b = s.signsBlocks[i];
        if (!b) continue;
        const len = (parseInt(b.size, 10) === 4) ? 5 : 3;
        const fallback = defs ? defs.signsStarts[i] : [45, 55, 95, 105][i];
        const cur = parseInt(b.start, 10) || fallback;
        const valid = findFreeStart(cur, len, 10, maxClashHole);
        b.start = valid;
        occupyRange(valid, len);
        setDomValue(`optSigns${i + 1}Hole`, valid);
      }
    }

    // 9. Stop Sign
    if (s.optStopSign && Array.isArray(s.stopSignHoles)) {
      const defaultStops121 = [20, 35, 50, 65, 80, 95, 105, 115];
      const defaultStopsClash = defs ? defs.stopSignHoles : null;
      const count = Math.min(8, Math.max(1, parseInt(s.stopSignCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defaultStopsClash ? (defaultStopsClash[i] || (35 + i * 50)) : (defaultStops121[i] || (15 + i * 12));
        const cur = parseInt(s.stopSignHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 5, maxStopSignHole);
        s.stopSignHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optStopSign${i + 1}Hole`, valid);
      }
    }

    // 10. Dice
    if (s.optDice && Array.isArray(s.diceHoles)) {
      const defaultDice121 = [20, 50, 80, 100];
      const defaultDiceClash = defs ? defs.diceHoles : null;
      const count = Math.min(4, Math.max(1, parseInt(s.diceCount || s.optDiceCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defaultDiceClash ? (defaultDiceClash[i] || (50 + i * 100)) : (defaultDice121[i] || (20 + i * 25));
        const cur = parseInt(s.diceHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 5, maxClashHole);
        s.diceHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optDice${i + 1}Hole`, valid);
      }
    }

    // 11. Spinning Wheel
    if ((s.optWheel || s.optSpinningWheel) && Array.isArray(s.wheelHoles)) {
      const defaultWheel121 = [15, 45, 75, 105];
      const defaultWheelClash = defs ? defs.wheelHoles : null;
      const count = Math.min(4, Math.max(1, parseInt(s.wheelCount || s.optWheelCount || s.optSpinningWheelCount, 10) || 1));
      for (let i = 0; i < count; i++) {
        const fallback = defaultWheelClash ? (defaultWheelClash[i] || (65 + i * 90)) : (defaultWheel121[i] || (15 + i * 30));
        const cur = parseInt(s.wheelHoles[i], 10) || fallback;
        const valid = findFreeStart(cur, 1, 5, maxClashHole);
        s.wheelHoles[i] = valid;
        occupyRange(valid, 1);
        setDomValue(`optWheel${i + 1}Hole`, valid);
      }
    }

    return s;
  }

  /**
   * Computes street or turn display name for a given hole number on 121 or Clash circular board.
   * @param {number} h
   * @param {number} [targetVal=121]
   * @returns {string}
   */
  static getStreetOrTurnName(h, targetVal = 121) {
    const isClashTarget = [241, 361, 481].includes(targetVal);
    if (isClashTarget) {
      if (h <= 0) return 'Start';
      if (h >= targetVal) return 'Finish';
      const row = Math.floor((h - 1) / 60);
      const pos = (h - 1) % 60;
      if (pos < 55) return `Street ${row + 1}`;
      return `Turn ${row + 1}`;
    }
    if (h > 70) return 'Street 3';
    if (h > 65) return 'Turn 2';
    if (h > 35) return 'Street 2';
    if (h > 30) return 'Turn 1';
    return 'Street 1';
  }

  /**
   * Updates jump option labels to show forward (1 hole) vs backward (3 holes).
   * @param {number} idx
   */
  static updateJumpOptionLabels(idx) {
    if (typeof document === 'undefined') return;
    const sEl = document.getElementById(`optJump${idx}Start`);
    const dEl = document.getElementById(`optJump${idx}Dest`);
    if (!sEl || !dEl) return;
    const dVal = parseInt(dEl.value, 10);
    Array.from(sEl.options).forEach(opt => {
      const sVal = parseInt(opt.value, 10);
      if (isNaN(sVal)) return;
      const isFwd = !isNaN(dVal) && dVal > sVal;
      opt.textContent = isFwd ? `Hole ${sVal}` : `Holes ${sVal}–${sVal + 2}`;
    });
  }

  /**
   * Toggles an obstacle panel on/off, updating its rows and overlap enforcement.
   * @param {string} type
   * @param {boolean} checked
   * @param {Object} [helpers]
   */
  static toggleObstacleConfig(type, checked, helpers = {}) {
    if (typeof document === 'undefined') return;
    const configMap = {
      noPassingLanes: { panelId: 'noPassingLanesConfig', count: 4, updateFn: 'updateNoPassingLaneRows', onField: 'onNoPassingLaneFieldChange' },
      jump: { panelId: 'jumpConfig', count: 8, updateFn: 'updateJumpRows', onField: 'onJumpFieldChange' },
      jail: { panelId: 'jailConfig', count: 2, updateFn: 'updateJailRows', onField: 'onJailFieldChange' },
      bar: { panelId: 'barConfig', count: 2, updateFn: 'updateBarRows', onField: 'onBarFieldChange' },
      timeout: { panelId: 'timeoutConfig', count: 2, updateFn: 'updateTimeoutRows', onField: 'onTimeoutFieldChange' },
      detour: { panelId: 'detourConfig', count: 2, updateFn: 'updateDetourRows', onField: 'onDetourFieldChange' },
      roundAbout: { panelId: 'roundAboutConfig', count: 2, updateFn: 'updateRoundAboutRows', onField: 'onRoundAboutFieldChange' },
      signs: { panelId: 'signsConfig', count: 4, updateFn: 'updateSignsRows', onField: 'onSignsFieldChange' },
      stopSign: { panelId: 'stopSignConfig', count: 8, updateFn: 'updateStopSignRows', onField: 'onStopSignFieldChange' },
      dice: { panelId: 'diceConfig', count: 4, updateFn: 'updateDiceRows', onField: 'onDiceFieldChange' },
      wheel: { panelId: 'wheelConfig', count: 4, updateFn: 'updateWheelRows', onField: 'onWheelFieldChange' }
    };
    const cfg = configMap[type];
    if (!cfg) return;

    const panel = document.getElementById(cfg.panelId);
    if (panel) panel.style.display = checked ? 'flex' : 'none';

    if (typeof window !== 'undefined' && typeof window[cfg.updateFn] === 'function') {
      window[cfg.updateFn]();
    }
    if (cfg.onField && typeof window !== 'undefined' && typeof window[cfg.onField] === 'function') {
      for (let i = 1; i <= cfg.count; i++) {
        window[cfg.onField](i, true);
      }
    }
    if (checked) {
      if (typeof window !== 'undefined' && typeof window.enforceClashNoOverlap === 'function') {
        window.enforceClashNoOverlap();
      }
    } else {
      if (typeof window !== 'undefined' && typeof window.updateAllClashDropdowns === 'function') {
        window.updateAllClashDropdowns();
      }
    }
    if (typeof window !== 'undefined' && typeof window.readFormSettings === 'function' && typeof window.saveSettingsStore === 'function') {
      window.saveSettingsStore(window.readFormSettings());
    }
  }

  /**
   * Updates obstacle row visibility based on count dropdown.
   * @param {string} type
   */
  static updateObstacleRows(type) {
    if (typeof document === 'undefined') return;
    const def = ClashSettings.getBlockDefs().find(d => d.type === type);
    const rowMap = {
      noPassingLanes: { countId: 'optNoPassingLaneCount', prefix: 'nplLaneRow', max: 4 },
      jump: { countId: 'optJumpCount', prefix: 'jumpRow', max: 8 },
      jail: { countId: 'optJailCount', prefix: 'jailRow', max: 4 },
      bar: { countId: 'optBarCount', prefix: 'barRow', max: 4 },
      timeout: { countId: 'optTimeoutCount', prefix: 'timeoutRow', max: 4 },
      detour: { countId: 'optDetourCount', prefix: 'detourRow', max: 4 },
      roundAbout: { countId: 'optRoundAboutCount', prefix: 'roundAboutRow', max: 4 },
      signs: { countId: 'optSignsCount', prefix: 'signsRow', max: 8 },
      stopSign: { countId: 'optStopSignCount', prefix: 'stopSignRow', max: 8 },
      dice: { countId: 'optDiceCount', prefix: 'diceRow', max: 4 },
      wheel: { countId: 'optWheelCount', prefix: 'wheelRow', max: 4 }
    };
    const rm = rowMap[type];
    if (!rm) return;
    const max = (def && typeof def.maxCount === 'number') ? def.maxCount : rm.max;

    const countEl = document.getElementById(rm.countId);
    const count = countEl ? parseInt(countEl.value, 10) : 2;
    for (let i = 1; i <= max; i++) {
      const row = document.getElementById(rm.prefix + i);
      if (row) row.style.display = i <= count ? 'flex' : 'none';
    }

    if (typeof window !== 'undefined' && typeof window.enforceClashNoOverlap === 'function') {
      window.enforceClashNoOverlap();
    }
    if (typeof window !== 'undefined' && typeof window.readFormSettings === 'function' && typeof window.saveSettingsStore === 'function') {
      window.saveSettingsStore(window.readFormSettings());
    }
  }

  /**
   * Handles field change on obstacle dropdowns, formatting badge and enforcing non-overlap.
   * @param {string} type
   * @param {number} idx
   * @param {boolean} [skipSave=false]
   */
  static onObstacleFieldChange(type, idx, skipSave = false) {
    if (typeof document === 'undefined') return;
    const targetVal = Number(
      (typeof window !== 'undefined' && window.settings && window.settings.target) ||
      (document.getElementById('optTarget') && document.getElementById('optTarget').value) ||
      121
    );
    const isClashTarget = [241, 361, 481].includes(targetVal);
    const defs = isClashTarget ? getClashDefaults(targetVal) : null;

    if (type === 'noPassingLanes') {
      if (typeof window !== 'undefined' && typeof window.enforceClashNoOverlap === 'function') {
        window.enforceClashNoOverlap(`optNoPassingLane${idx}Start`);
      }
    } else if (type === 'jump') {
      ClashSettings.updateJumpOptionLabels(idx);
      if (typeof window !== 'undefined' && typeof window.enforceClashNoOverlap === 'function') {
        window.enforceClashNoOverlap(`optJump${idx}Start`);
      }
      const sEl = document.getElementById(`optJump${idx}Start`);
      const dEl = document.getElementById(`optJump${idx}Dest`);
      const bEl = document.getElementById(`jumpBadge${idx}`);
      const rowEl = document.getElementById(`jumpRow${idx}`);
      if (sEl && dEl && bEl) {
        const sFallback = defs ? defs.jumpStarts[idx - 1] : [15, 65, 5, 115, 25, 75, 45, 95][idx - 1];
        const dFallback = defs ? defs.jumpDests[idx - 1] : [35, 50, 45, 70, 55, 60, 80, 85][idx - 1];
        const s = parseInt(sEl.value, 10) || sFallback;
        const d = parseInt(dEl.value, 10) || dFallback;
        const isForward = d > s;
        bEl.className = 'jump-badge ' + (isForward ? 'jump-badge-forward' : 'jump-badge-backward');
        bEl.textContent = isForward ? 'Forward (1 hole) ➔' : 'Backward (3 holes) ⬅';
        if (rowEl) rowEl.classList.toggle('backward', !isForward);
      }
      if (typeof window !== 'undefined' && typeof window.syncClashPlacementButton === 'function') {
        window.syncClashPlacementButton(`optJump${idx}Start`);
        window.syncClashPlacementButton(`optJump${idx}Dest`);
      }
    } else if (['jail', 'bar', 'timeout', 'detour', 'roundAbout', 'stopSign', 'dice', 'wheel'].includes(type)) {
      const idPrefix = {
        jail: 'optJail',
        bar: 'optBar',
        timeout: 'optTimeout',
        detour: 'optDetour',
        roundAbout: 'optRoundAbout',
        stopSign: 'optStopSign',
        dice: 'optDice',
        wheel: 'optWheel'
      }[type];
      const badgePrefix = {
        jail: 'jailBadge',
        bar: 'barBadge',
        timeout: 'timeoutBadge',
        detour: 'detourBadge',
        roundAbout: 'roundAboutBadge',
        stopSign: 'stopSignBadge',
        dice: 'diceBadge',
        wheel: 'wheelBadge'
      }[type];

      if (typeof window !== 'undefined' && typeof window.enforceClashNoOverlap === 'function') {
        window.enforceClashNoOverlap(`${idPrefix}${idx}Hole`);
      }
      const hEl = document.getElementById(`${idPrefix}${idx}Hole`);
      const bEl = document.getElementById(`${badgePrefix}${idx}`);
      if (hEl && bEl) {
        let fallback = 25;
        if (type === 'jail') fallback = defs ? defs.jailHoles[idx - 1] : (idx === 1 ? 25 : 75);
        if (type === 'bar') fallback = defs ? defs.barHoles[idx - 1] : (idx === 1 ? 40 : 85);
        if (type === 'timeout') fallback = defs ? defs.timeoutHoles[idx - 1] : (idx === 1 ? 30 : 90);
        if (type === 'detour') fallback = defs ? defs.detourHoles[idx - 1] : (idx === 1 ? 10 : 60);
        if (type === 'roundAbout') fallback = defs ? defs.roundAboutHoles[idx - 1] : (idx === 1 ? 35 : 70);
        if (type === 'stopSign') {
          const defaultStops121 = [20, 35, 50, 65, 80, 95, 105, 115];
          const defaultStopsClash = defs ? defs.stopSignHoles : null;
          fallback = defaultStopsClash ? (defaultStopsClash[idx - 1] || (35 + idx * 50)) : (defaultStops121[idx - 1] || (15 + idx * 12));
        }
        if (type === 'dice') {
          const defaultDice121 = [20, 50, 80, 100];
          const defaultDiceClash = defs ? defs.diceHoles : null;
          fallback = defaultDiceClash ? (defaultDiceClash[idx - 1] || (50 + idx * 100)) : (defaultDice121[idx - 1] || (20 + idx * 25));
        }
        if (type === 'wheel') {
          const defaultWheel121 = [15, 45, 75, 105];
          const defaultWheelClash = defs ? defs.wheelHoles : null;
          fallback = defaultWheelClash ? (defaultWheelClash[idx - 1] || (65 + idx * 90)) : (defaultWheel121[idx - 1] || (15 + idx * 30));
        }

        const h = parseInt(hEl.value, 10) || fallback;
        const streetName = ClashSettings.getStreetOrTurnName(h, targetVal);
        bEl.textContent = `${streetName} (Hole ${h})`;
      }
    } else if (type === 'signs') {
      if (typeof window !== 'undefined' && typeof window.enforceClashNoOverlap === 'function') {
        window.enforceClashNoOverlap(`optSigns${idx}Hole`);
      }
      const hEl = document.getElementById(`optSigns${idx}Hole`);
      const sEl = document.getElementById(`optSigns${idx}Size`);
      const bEl = document.getElementById(`signsBadge${idx}`);
      const slot3Wrap = document.getElementById(`signs${idx}Slot3Wrap`);
      const slot4Wrap = document.getElementById(`signs${idx}Slot4Wrap`);
      const size = sEl ? parseInt(sEl.value, 10) : 2;
      if (slot3Wrap) slot3Wrap.style.display = size === 4 ? 'flex' : 'none';
      if (slot4Wrap) slot4Wrap.style.display = size === 4 ? 'flex' : 'none';
      if (hEl && bEl) {
        const fallback = defs ? defs.signsStarts[idx - 1] : (idx === 1 ? 45 : (idx === 2 ? 55 : (idx === 3 ? 95 : 105)));
        const start = parseInt(hEl.value, 10) || fallback;
        const pegLength = size === 2 ? 3 : 5;
        const end = start + pegLength - 1;
        const streetName = ClashSettings.getStreetOrTurnName(start, targetVal);
        bEl.textContent = `${streetName} (Holes ${start}–${end} · ${size} Signs)`;
      }
    }

    if (!skipSave && typeof window !== 'undefined' && typeof window.readFormSettings === 'function' && typeof window.saveSettingsStore === 'function') {
      window.saveSettingsStore(window.readFormSettings());
    }
  }

  /**
   * Reads configured sign blocks from form DOM elements.
   * @param {Object} [options]
   * @returns {Array<Object>}
   */
  static readSignsBlocksFromForm(options = {}) {
    if (typeof document === 'undefined') return [];
    const targetVal = Number(
      options.target ||
      (typeof window !== 'undefined' && window.settings && window.settings.target) ||
      (document.getElementById('optTarget') && document.getElementById('optTarget').value) ||
      121
    );
    const isClashTarget = [241, 361, 481].includes(targetVal);
    const defs = isClashTarget ? getClashDefaults(targetVal) : null;
    const blocks = [];

    for (let i = 1; i <= 4; i++) {
      const hEl = document.getElementById(`optSigns${i}Hole`);
      const sEl = document.getElementById(`optSigns${i}Size`);
      const fallback = defs ? defs.signsStarts[i - 1] : (i === 1 ? 45 : (i === 2 ? 55 : (i === 3 ? 95 : 105)));
      const start = hEl ? parseInt(hEl.value, 10) : fallback;
      const size = sEl ? parseInt(sEl.value, 10) : (i % 2 === 1 ? 2 : 4);
      const signs = [];
      for (let slot = 1; slot <= size; slot++) {
        const slotEl = document.getElementById(`optSigns${i}Slot${slot}`);
        if (slotEl && slotEl.value) {
          signs.push(slotEl.value);
        }
      }
      if (!signs.length) {
        if (size === 2) signs.push('Plus 5', 'Roll The Dice');
        else signs.push('Spin The Wheel', 'Plus 10', 'Minus 5', 'Score Plus x2');
      }
      blocks.push({ id: i, start, size, signs });
    }
    return blocks;
  }

  /**
   * Toggles skunk markers configuration panel and updates settings.
   * @param {boolean} checked
   * @param {Object} [helpers]
   */
  static toggleSkunkMarkersConfig(checked, helpers = {}) {
    if (typeof document === 'undefined') return;
    const panel = document.getElementById('skunkMarkersConfig');
    if (panel) panel.style.display = checked ? 'flex' : 'none';
    const settings = helpers.settings || (typeof window !== 'undefined' ? window.settings : null);
    if (settings) {
      settings.optSkunkMarkers = Boolean(checked);
    }
    const onSkunk = helpers.onSkunkMarkerFieldChange || (typeof window !== 'undefined' ? window.onSkunkMarkerFieldChange : ClashSettings.onSkunkMarkerFieldChange);
    const onDoubleSkunk = helpers.onDoubleSkunkMarkerFieldChange || (typeof window !== 'undefined' ? window.onDoubleSkunkMarkerFieldChange : ClashSettings.onDoubleSkunkMarkerFieldChange);
    if (typeof onSkunk === 'function') onSkunk(true, helpers);
    if (typeof onDoubleSkunk === 'function') onDoubleSkunk(true, helpers);

    const syncPlacement = helpers.syncAllClashPlacementButtons || (typeof window !== 'undefined' ? window.syncAllClashPlacementButtons : null);
    if (typeof syncPlacement === 'function') syncPlacement();

    const readSettings = helpers.readFormSettings || (typeof window !== 'undefined' ? window.readFormSettings : null);
    const saveSettings = helpers.saveSettingsStore || (typeof window !== 'undefined' ? window.saveSettingsStore : null);
    if (typeof readSettings === 'function' && typeof saveSettings === 'function') {
      saveSettings(readSettings());
    }

    const gameBoard = document.getElementById('board');
    const buildBoard = helpers.buildBoard || (typeof window !== 'undefined' ? window.buildBoard : null);
    if (gameBoard && gameBoard.querySelector('svg') && typeof buildBoard === 'function') {
      buildBoard();
    }
  }

  /**
   * Updates skunk marker hole value and badge text.
   * @param {boolean} [skipSave]
   * @param {Object} [helpers]
   */
  static onSkunkMarkerFieldChange(skipSave = false, helpers = {}) {
    if (typeof document === 'undefined') return;
    const hEl = document.getElementById('optSkunkMarkerHole');
    const bEl = document.getElementById('skunkMarkerBadge');
    const settings = helpers.settings || (typeof window !== 'undefined' ? window.settings : null);
    const targetVal = Number((settings && settings.target) || (document.getElementById('optTarget') && document.getElementById('optTarget').value) || 481);
    const getSkunkDefs = helpers.getSkunkDefaults || (typeof window !== 'undefined' ? window.getSkunkDefaults : null);
    const defs = typeof getSkunkDefs === 'function' ? getSkunkDefs(targetVal) : { skunkHole: 360, doubleSkunkHole: 240 };
    const h = (hEl && parseInt(hEl.value, 10)) || defs.skunkHole;
    if (settings) {
      settings.skunkMarkerHole = h;
    }
    if (bEl) {
      const getStreet = helpers.getStreetOrTurnName || (typeof window !== 'undefined' ? window.getStreetOrTurnName : null);
      const sName = typeof getStreet === 'function' ? getStreet(h) : '';
      bEl.textContent = `${sName ? sName + ' · ' : ''}Hole ${h}`;
    }
    const readSettings = helpers.readFormSettings || (typeof window !== 'undefined' ? window.readFormSettings : null);
    const saveSettings = helpers.saveSettingsStore || (typeof window !== 'undefined' ? window.saveSettingsStore : null);
    if (!skipSave && typeof readSettings === 'function' && typeof saveSettings === 'function') {
      saveSettings(readSettings());
    }
    const gameBoard = document.getElementById('board');
    const buildBoard = helpers.buildBoard || (typeof window !== 'undefined' ? window.buildBoard : null);
    if (gameBoard && gameBoard.querySelector('svg') && typeof buildBoard === 'function' && settings && settings.optSkunkMarkers) {
      buildBoard();
    }
  }

  /**
   * Updates double skunk marker hole value and badge text.
   * @param {boolean} [skipSave]
   * @param {Object} [helpers]
   */
  static onDoubleSkunkMarkerFieldChange(skipSave = false, helpers = {}) {
    if (typeof document === 'undefined') return;
    const hEl = document.getElementById('optDoubleSkunkMarkerHole');
    const bEl = document.getElementById('doubleSkunkMarkerBadge');
    const settings = helpers.settings || (typeof window !== 'undefined' ? window.settings : null);
    const targetVal = Number((settings && settings.target) || (document.getElementById('optTarget') && document.getElementById('optTarget').value) || 481);
    const getSkunkDefs = helpers.getSkunkDefaults || (typeof window !== 'undefined' ? window.getSkunkDefaults : null);
    const defs = typeof getSkunkDefs === 'function' ? getSkunkDefs(targetVal) : { skunkHole: 360, doubleSkunkHole: 240 };
    const h = (hEl && parseInt(hEl.value, 10)) || defs.doubleSkunkHole;
    if (settings) {
      settings.doubleSkunkMarkerHole = h;
    }
    if (bEl) {
      const getStreet = helpers.getStreetOrTurnName || (typeof window !== 'undefined' ? window.getStreetOrTurnName : null);
      const sName = typeof getStreet === 'function' ? getStreet(h) : '';
      bEl.textContent = `${sName ? sName + ' · ' : ''}Hole ${h}`;
    }
    const readSettings = helpers.readFormSettings || (typeof window !== 'undefined' ? window.readFormSettings : null);
    const saveSettings = helpers.saveSettingsStore || (typeof window !== 'undefined' ? window.saveSettingsStore : null);
    if (!skipSave && typeof readSettings === 'function' && typeof saveSettings === 'function') {
      saveSettings(readSettings());
    }
    const gameBoard = document.getElementById('board');
    const buildBoard = helpers.buildBoard || (typeof window !== 'undefined' ? window.buildBoard : null);
    if (gameBoard && gameBoard.querySelector('svg') && typeof buildBoard === 'function' && settings && settings.optSkunkMarkers) {
      buildBoard();
    }
  }

  /**
   * Checks for obstacle range collision against active obstacles.
   */
  static checkCollision(candidateStart, length, excludeSelectId, blockList, clashManager = null) {
    const cm = clashManager || (typeof window !== 'undefined' ? window.ClashManager : null);
    if (cm && typeof cm.checkCollision === 'function') {
      return cm.checkCollision(candidateStart, length, excludeSelectId, blockList);
    }
    const end = candidateStart + length - 1;
    for (const b of blockList || []) {
      if (b.selectId === excludeSelectId) continue;
      if (candidateStart <= b.end && end >= b.start) return b;
    }
    return null;
  }

  /**
   * Discovers an available non-overlapping hole for a select dropdown.
   */
  static findAvailableHole(sel, length, excludeSelectId, activeBlocks, clashManager = null) {
    if (!sel) return null;
    const currentVal = parseInt(sel.value, 10);
    const options = Array.from(sel.options || [])
      .map(o => parseInt(o.value, 10))
      .filter(v => !isNaN(v));

    const cm = clashManager || (typeof window !== 'undefined' ? window.ClashManager : null);
    if (cm && typeof cm.findAvailableHole === 'function') {
      return cm.findAvailableHole(options, currentVal, length, excludeSelectId, activeBlocks);
    }
    return currentVal;
  }

  /**
   * Enforces non-overlapping placement across all active obstacle dropdown selectors.
   */
  static enforceNoOverlap(initiatorSelectId = null, context = {}) {
    if (typeof window !== 'undefined' && (window._isEnforcingClashNoOverlap || window._isRandomizingClash)) return;
    if (typeof window !== 'undefined') window._isEnforcingClashNoOverlap = true;

    const getActiveClashBlocks = context.getActiveClashBlocks || (typeof window !== 'undefined' ? window.getActiveClashBlocks : () => []);
    const updateAllClashDropdowns = context.updateAllClashDropdowns || (typeof window !== 'undefined' ? window.updateAllClashDropdowns : () => {});
    const checkCollision = context.checkCollision || ClashSettings.checkCollision;
    const findAvailableHole = context.findAvailableHole || ClashSettings.findAvailableHole;

    try {
      const active = getActiveClashBlocks();
      const blocksToProcess = [...active];
      if (initiatorSelectId) {
        const initIdx = blocksToProcess.findIndex(b => b.selectId === initiatorSelectId);
        if (initIdx > 0) {
          const [initBlock] = blocksToProcess.splice(initIdx, 1);
          blocksToProcess.unshift(initBlock);
        }
      }

      const resolved = [];
      for (const b of blocksToProcess) {
        const sel = typeof document !== 'undefined' ? document.getElementById(b.selectId) : null;
        if (!sel) continue;
        let start = parseInt(sel.value, 10);
        const collision = checkCollision(start, b.length, b.selectId, resolved);
        if (collision) {
          const freeHole = findAvailableHole(sel, b.length, b.selectId, resolved);
          if (freeHole != null && freeHole !== start) {
            sel.value = String(freeHole);
            start = freeHole;
            if (b.def && typeof b.def.onFieldChange === 'function') {
              b.def.onFieldChange(b.index, true);
            }
          }
        }
        resolved.push({
          ...b,
          start,
          end: start + b.length - 1
        });
      }

      updateAllClashDropdowns();
    } finally {
      if (typeof window !== 'undefined') window._isEnforcingClashNoOverlap = false;
    }
  }

  toggleSkunkMarkersConfig(checked, helpers) {
    return ClashSettings.toggleSkunkMarkersConfig(checked, helpers);
  }

  onSkunkMarkerFieldChange(skipSave, helpers) {
    return ClashSettings.onSkunkMarkerFieldChange(skipSave, helpers);
  }

  onDoubleSkunkMarkerFieldChange(skipSave, helpers) {
    return ClashSettings.onDoubleSkunkMarkerFieldChange(skipSave, helpers);
  }

  checkCollision(candidateStart, length, excludeSelectId, blockList, clashManager) {
    return ClashSettings.checkCollision(candidateStart, length, excludeSelectId, blockList, clashManager);
  }

  findAvailableHole(sel, length, excludeSelectId, activeBlocks, clashManager) {
    return ClashSettings.findAvailableHole(sel, length, excludeSelectId, activeBlocks, clashManager);
  }

  enforceNoOverlap(initiatorSelectId, context) {
    return ClashSettings.enforceNoOverlap(initiatorSelectId, context);
  }
}

export function getClashBlockDefs() {
  return ClashSettings.getBlockDefs();
}


